import { useMsal } from "@azure/msal-react";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import {
    LS_KEY_REFRESH_TOKEN_EXP_DATE,
    LS_KEY_TAB_COUNT_AUTH_CONTROL,
    SSO_AUTHORITIES,
    SSO_QS_KEY_DOMAIN_HINT,
    SSO_SCOPE,
} from "../../../constants";
import { useTabTracker } from "../../../hooks/useTabTracker";
import { useTabVisibility } from "../../../hooks/useTabVisibility";
import { AuthState } from "../enums";
import { useSsoQueryParameters } from "../hooks/useSsoQueryParameters";

import { IMainAuthContext, MainAuthContextProvider, useMainAuth } from "./mainAuthContext";
import { MsalContextProvider } from "./msalContext";

type IAuthContext = {
    logout: () => void;
    changePassword: () => void;
    state: AuthState;
    msalRedirectHandle: (force?: boolean) => Promise<void>;
    msalLogin: (loginHint?: string, domainHint?: string) => Promise<void>;
} & IMainAuthContext;

const AuthContext = createContext<IAuthContext | null>(null);

function AuthContextProvider({ children }: React.PropsWithChildren<unknown>) {
    const { instance } = useMsal();
    const mainAuth = useMainAuth();
    const { isTabVisible, isTabFocused } = useTabVisibility();
    const {
        ssoQueryParameters,
        setDomainHint,
        domainHintFromParams,
    } = useSsoQueryParameters();

    useTabTracker({
        trackingLsKey: LS_KEY_TAB_COUNT_AUTH_CONTROL,
        onAllTabsClosed: mainAuth.mainLogout,
        disabled: !mainAuth.isFederatedSsoUser,
    });

    const [state, setState] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stateFromUrl = urlParams.get("state");
        if (stateFromUrl) {
            let currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete("state");
            window.history.replaceState({}, document.title, currentUrl.toString());
            if (stateFromUrl === AuthState.LoggedOut) {
                mainAuth.mainLogout();
            }
            return stateFromUrl as AuthState;
        }
        return AuthState.Idle;
    });

    const msalLogin = useCallback(async (loginHint?: string, domainHint?: string) => {
        setState(AuthState.MsalLogin);
        if (domainHint !== undefined) setDomainHint(domainHint);
        await instance.initialize();
        await instance.loginRedirect({
            authority: SSO_AUTHORITIES.paznicCustom,
            scopes: [SSO_SCOPE],
            prompt: "login",
            state: AuthState.Idle,
            loginHint: loginHint,
            extraQueryParameters: domainHint === undefined
                ? ssoQueryParameters
                : { ...ssoQueryParameters, [SSO_QS_KEY_DOMAIN_HINT]: domainHint },
        });
    }, [instance, ssoQueryParameters, setDomainHint]);

    const msalRedirectHandle = useCallback(async () => {
        setState(AuthState.MsalRedirectHandle);

        try {
            await instance.initialize();
            const response = await instance.handleRedirectPromise();
            if (response && response.accessToken) {
                setState(AuthState.MainTokenRequest);
                mainAuth.mainLogin(response.accessToken);
            } else {
                setState(prev => prev === AuthState.MsalLogin ? prev : AuthState.DomainHintAccess);
            }
        } catch (error) {
            console.error(error);
            setState(AuthState.Error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, mainAuth.mainLogin, msalLogin]);

    const changePassword = useCallback(async () => {
        try {
            await instance.loginRedirect({
                authority: SSO_AUTHORITIES.changePassword,
                scopes: [SSO_SCOPE],
                prompt: "login",
                extraQueryParameters: {
                    hint: mainAuth.email || "",
                    ...ssoQueryParameters,
                },
            });
        } catch (error) {
            console.error("Password change failed:", error);
        }
    }, [instance, mainAuth.email, ssoQueryParameters]);

    const logout = useCallback(async () => {
        let postLogoutRedirectUri: string | undefined = window.location.origin;

        if (ssoQueryParameters.domain_hint !== null) {
            postLogoutRedirectUri += `?domain_hint=${ssoQueryParameters.domain_hint}`
        }

        try {
            await instance.logout({
                authority: SSO_AUTHORITIES.paznicCustom,
                state: AuthState.LoggedOut,
                postLogoutRedirectUri: postLogoutRedirectUri,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setState(AuthState.LoggedOut);
        }
    }, [instance, ssoQueryParameters]);

    useEffect(() => {
        setState(prev => mainAuth.loginErrors.length > 0
            ? AuthState.Error
            : prev === AuthState.Error
                ? AuthState.Idle
                : prev);
    }, [mainAuth.loginErrors]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (mainAuth.isAuthenticated) {
            interval = setInterval(() => {
                const expDate = localStorage.getItem(LS_KEY_REFRESH_TOKEN_EXP_DATE);
                if (expDate == null || new Date(expDate) < new Date()) {
                    console.debug('token expired: ' + expDate);
                    logout();
                    clearInterval(interval);
                }
            }, 2000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [logout, mainAuth.isAuthenticated]);

    useEffect(() => {
        if (mainAuth.username || state !== AuthState.Idle || !isTabVisible || !isTabFocused) return;
        msalRedirectHandle();
    }, [msalRedirectHandle, mainAuth.username, state, isTabVisible, isTabFocused]);

    useEffect(() => {
        if (domainHintFromParams && state !== AuthState.LoggedOut) {
            msalLogin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainHintFromParams]);

    return (
        <AuthContext.Provider value={{
            ...mainAuth,
            msalRedirectHandle,
            msalLogin,
            logout,
            changePassword,
            state,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function FullAuthContextProvider({ children }: React.PropsWithChildren<unknown>) {
    return (
        <MsalContextProvider>
            <MainAuthContextProvider>
                <AuthContextProvider>
                    {children}
                </AuthContextProvider>
            </MainAuthContextProvider>
        </MsalContextProvider>
    );
}


export function useAuth() {
    const value = useContext(AuthContext);

    if (!value) {
        throw new Error("AuthContext consumed outside of AuthContextProvider scope");
    }

    return value;
}
